// Sentry browser SDK init — loaded by instrumentation-client.ts.
// DSN-gated: without NEXT_PUBLIC_SENTRY_DSN nothing is imported or initialised,
// so deployments without Sentry pay no bundle cost.
// openspec: iso27001-soc2-readiness task 1.4
// Follow-up: wrap next.config.js with withSentryConfig for source-map upload
// and automatic router instrumentation (owned by the next.config.js change).

import { NOISE_PATTERNS } from '@/lib/error-noise'

const dsn = process.env.NEXT_PUBLIC_SENTRY_DSN

/**
 * Known non-actionable browser / engine noise.
 * EmptyRanges: WebKit NullMedia.buffered/played/seekable bug (webkit#318284)
 * that surfaces on iOS when a media controller outlives its <video> (STYTRIX-WEB-A).
 */
const IGNORED_ERRORS: Array<string | RegExp> = [
  "Can't find variable: EmptyRanges",
  /Can't find variable:\s*EmptyRanges/,
  'ReferenceError: EmptyRanges',
  // Extension / ad-blocker / browser-quirk noise, shared with PostHog.
  ...NOISE_PATTERNS,
]

function isEmptyRangesNoise(message: unknown): boolean {
  return typeof message === 'string' && message.includes('EmptyRanges')
}

type BrowserException = {
  type?: string
  value?: string
  stacktrace?: { frames?: Array<{
    filename?: string
    abs_path?: string
    function?: string
    lineno?: number
    colno?: number
  }> }
}

// Attribution only: retain the event, severity, fingerprint and original stack.
// A mixed application/extension stack is deliberately left unclassified.
export function browserErrorSource(values: BrowserException[] | undefined):
  'browser_extension' | 'suspected_browser_extension' | undefined {
  if (!values?.length) return undefined
  const sources = values.map((exception) => {
    const frames = exception.stacktrace?.frames
    if (!frames?.length) return undefined
    if (frames.every((frame) =>
      /^(?:chrome-extension|moz-extension|safari-web-extension):\/\//.test(frame.abs_path || frame.filename || ''),
    )) return 'browser_extension' as const

    // Sentry may normalize the original extension URL to app:///. This exact
    // two-frame signature matches the independently reported M_ID extension
    // crash, but without the original scheme it remains a suspicion, not proof.
    if (exception.type === 'TypeError' &&
      exception.value === "Cannot read properties of undefined (reading 'M_ID')" &&
      frames.length === 2 && frames.every((frame) =>
        (frame.abs_path || frame.filename) === 'app:///executors/200.js' && frame.lineno === 1,
      ) && frames[0].function === 'E' && frames[0].colno === 1442 &&
      frames[1].function === 'Y' && frames[1].colno === 761) {
      return 'suspected_browser_extension' as const
    }
    return undefined
  })
  if (sources.some((source) => source === undefined)) return undefined
  return sources.every((source) => source === 'browser_extension')
    ? 'browser_extension' : 'suspected_browser_extension'
}

export function initSentryClient(): void {
  if (!dsn || typeof window === 'undefined') return
  import('@sentry/nextjs')
    .then((Sentry) => {
      Sentry.init({
        dsn,
        tracesSampleRate: 0.05,
        sendDefaultPii: false,
        environment: process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT || process.env.NEXT_PUBLIC_VERCEL_ENV || process.env.VERCEL_ENV || 'development',
        ignoreErrors: IGNORED_ERRORS,
        beforeSend(event, hint) {
          const message =
            event.message ||
            event.exception?.values?.[0]?.value ||
            (hint?.originalException as Error | undefined)?.message ||
            ''
          if (isEmptyRangesNoise(message)) return null
          const source = browserErrorSource(event.exception?.values)
          if (source) event.tags = { ...event.tags, error_source: source }
          return event
        },
      })
    })
    .catch((err) => {
      console.warn('[sentry] client init skipped:', (err as Error)?.message)
    })
}
